import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Box, MenuItem, Typography } from '@mui/material';
import NkySpinner from '../NkySpinner/NkySpinner';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import { serviceWizardSearchRequestService } from '../../../services/api/serviceWizardSearchRequestService';
import { accountService } from '../../../services/api/accountService';
import { getOrCreateWizardClientSessionId } from '../../../utils/wizardClientSession';
import { isAllowedWizardPhotoFile } from '../../../utils/wizardPhotoUpload';
import { useAuth } from '../../../context/AuthContext';
import { setSendInquiryResume } from '../../../utils/sendInquiryResume';
import {
  formatPhoneInputMask,
  phoneDigitsForApi,
} from '../../../utils/phoneInputMask';
import {
  getSignupEmailError,
  getSignupPhoneError,
  SIGNUP_EMAIL_REGEX,
} from '../../../utils/signupFieldValidation';
import { getNavigateToLoginOptions } from '../../../utils/authSessionExpired';
import {
  writeWizardRequesterCache,
} from '../../../utils/wizardRequesterSessionCache';
import { toPersonNameCase } from '../../../utils/normalizePersonName';
import { SERVICE_AREA_STATE_CODES } from '../../../utils/serviceAreaStates';
import {
  GuestFormSection,
  GuestFormGrid,
  ContactField,
  ContactExistingAccountPanel,
  ContactExistingAccountIconWrap,
  ContactExistingAccountBody,
  ContactExistingAccountTitle,
  ContactExistingAccountMessage,
  ContactSignInButton,
  ContactDisclaimerText,
  ContactMethodSection,
  ContactMethodLabel,
  ContactMethodOptions,
  ContactMethodButton,
  ContactMethodSavedNote,
} from '../ContactForServiceModal/ContactForServiceModal.styled';
import {
  StyledDialog,
  DialogHeader,
  HeaderMain,
  HeaderIconWrap,
  HeaderTitle,
  HeaderSubtitle,
  DialogCloseButton,
  DialogBody,
  DialogFooter,
  PrimaryButton,
  SecondaryButton,
  DetailsField,
  PhotoRow,
  PhotoThumb,
  AddPhotoButton,
  UrgencySection,
  SectionLabel,
  UrgencyOptionsList,
  UrgencyOptionButton,
  LocationSection,
  LocationHint,
  CityStateRow,
  DetailsSection,
  PhotosSection,
  FormErrorAlert,
} from './CustomServiceInquiryModal.styled';
import {
  WIZARD_URGENCY_OPTIONS,
  getWizardUrgencyLabel,
} from '../../../constants/wizardUrgencyOptions';

const PREFERRED_CONTACT_METHODS = ['email', 'phone', 'text'];

function isPreferredContactMethod(value) {
  return PREFERRED_CONTACT_METHODS.includes(value);
}

function preferredContactMethodLabel(method) {
  if (method === 'email') return 'email';
  if (method === 'phone') return 'phone';
  if (method === 'text') return 'text';
  return method;
}

function optionalPhoneError(phoneValue) {
  return getSignupPhoneError(phoneValue, { requiredLabel: '' });
}

function formatLocationSummary(city, state) {
  const c = (city || '').trim();
  const st = (state || '').trim() || 'KY';
  if (!c) return '';
  return st ? `${c}, ${st}` : c;
}

export default function CustomServiceInquiryModal({
  open,
  onClose,
  onSent,
  service,
  provider,
  businessId,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const [details, setDetails] = useState('');
  const [urgency, setUrgency] = useState(null);
  const [city, setCity] = useState('');
  const [state, setState] = useState('KY');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [preferredContactMethod, setPreferredContactMethod] = useState('');
  /** True when the logged-in account already has a saved preference (do not re-ask). */
  const [contactMethodLocked, setContactMethodLocked] = useState(false);
  /** True while fetching account preference for a logged-in user. */
  const [contactMethodLoading, setContactMethodLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [emailHasAccount, setEmailHasAccount] = useState(false);
  const [guestFieldErrors, setGuestFieldErrors] = useState({ email: '', phone: '' });
  /** Set when create succeeds but PATCH requester returns 403 (email has account). */
  const [pendingResume, setPendingResume] = useState(null);

  const serviceType = service?._parentServiceType || service?.ServiceType;
  const subServiceType = service?.SubServiceType;
  const serviceTitle = service?.ProductName || service?.Title || 'Service inquiry';
  const resolvedBusinessId = businessId || provider?.BusinessId;

  const reset = () => {
    setDetails('');
    setUrgency(null);
    setCity('');
    setState('KY');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setPreferredContactMethod('');
    setContactMethodLocked(false);
    setContactMethodLoading(false);
    setPhotos([]);
    setError(null);
    setSubmitting(false);
    setEmailHasAccount(false);
    setGuestFieldErrors({ email: '', phone: '' });
    setPendingResume(null);
  };

  useEffect(() => {
    if (!open) return;
    setError(null);
    setEmailHasAccount(false);
    setPendingResume(null);
    setPreferredContactMethod('');
    setContactMethodLocked(false);
    if (!user) {
      setContactMethodLoading(false);
      return;
    }
    let cancelled = false;
    setContactMethodLoading(true);
    (async () => {
      try {
        const acc = await accountService.getAccount();
        if (cancelled) return;
        if (isPreferredContactMethod(acc.PreferredContactMethod)) {
          setPreferredContactMethod(acc.PreferredContactMethod);
          setContactMethodLocked(true);
        }
      } catch {
        /* keep unlocked selector if account fetch fails */
      } finally {
        if (!cancelled) setContactMethodLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user]);

  const handlePreferredContactMethodChange = (method) => {
    if (contactMethodLocked) return;
    setPreferredContactMethod(method);
    setGuestFieldErrors({ email: '', phone: '' });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailHasAccount(false);
    if (guestFieldErrors.email) {
      setGuestFieldErrors((prev) => ({ ...prev, email: '' }));
    }
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhoneInputMask(e.target.value));
    if (guestFieldErrors.phone) {
      setGuestFieldErrors((prev) => ({ ...prev, phone: '' }));
    }
  };

  const handleGoToSignIn = (wizardSearchRequestId, clientSessionId) => {
    if (resolvedBusinessId && wizardSearchRequestId) {
      setSendInquiryResume({
        returnPath: `${location.pathname}${location.search}`,
        businessId: resolvedBusinessId,
        businessName: provider?.BusinessName,
        wizardSearchRequestId,
        clientSessionId: clientSessionId || undefined,
      });
    }
    handleClose();
    navigate('/login', getNavigateToLoginOptions(location));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const validFiles = files.filter(isAllowedWizardPhotoFile);
    if (validFiles.length < files.length) {
      setError('Each photo must be JPG or PNG and under 5 MB.');
    }
    const newPhotos = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));
    setPhotos((prev) => [...prev, ...newPhotos].slice(0, 5));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!urgency) {
      setError('Please select when you need help.');
      return;
    }
    if (!details.trim()) {
      setError('Please describe what you need.');
      return;
    }
    if (!city.trim()) {
      setError('Please enter your city.');
      return;
    }
    if (!serviceType || !subServiceType) {
      setError('Missing service information. Please refresh and try again.');
      return;
    }
    if (!resolvedBusinessId) {
      setError('Missing business information. Please refresh and try again.');
      return;
    }

    if (!preferredContactMethod) {
      setError("Please select how you'd like to be contacted.");
      return;
    }

    let clientSessionId = null;
    if (!user) {
      clientSessionId = getOrCreateWizardClientSessionId();
      if (!firstName.trim() || !lastName.trim()) {
        setError('Please enter your first and last name.');
        return;
      }
      const emailErr =
        preferredContactMethod === 'email'
          ? getSignupEmailError(email)
          : '';
      const phoneErr =
        preferredContactMethod === 'phone' || preferredContactMethod === 'text'
          ? getSignupPhoneError(phone)
          : optionalPhoneError(phone);
      setGuestFieldErrors({ email: emailErr, phone: phoneErr });
      if (emailErr || phoneErr) {
        setError('Please correct your email or phone number.');
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    setEmailHasAccount(false);

    const cityTrim = city.trim();
    const stateTrim = state.trim() || 'KY';
    const locationLabel = formatLocationSummary(cityTrim, stateTrim);

    try {
      const photoUrls = await serviceWizardSearchRequestService.uploadWizardPhotos(photos, {
        clientSessionId: user ? null : clientSessionId,
      });

      const payload = {
        serviceType,
        subServiceType,
        inquiryKind: 'custom',
        targetSubServiceType: subServiceType,
        targetServiceTitle: serviceTitle,
        formData: {
          urgency,
          photos: photoUrls.filter(Boolean),
          details: details.trim(),
          city: cityTrim,
          state: stateTrim,
          additionalDetails: {},
        },
        requestSummary: {
          problemSummary: serviceTitle,
          urgency: getWizardUrgencyLabel(urgency),
          location: locationLabel,
        },
      };
      if (clientSessionId) {
        payload.clientSessionId = clientSessionId;
      }

      const res = await serviceWizardSearchRequestService.create(payload);

      if (!user && clientSessionId) {
        const emailTrim = email.trim();
        const digits = phoneDigitsForApi(phone);
        const normalizedFirstName = toPersonNameCase(firstName);
        const normalizedLastName = toPersonNameCase(lastName);
        setFirstName(normalizedFirstName);
        setLastName(normalizedLastName);
        const patchPayload = {
          firstName: normalizedFirstName,
          lastName: normalizedLastName,
          email:
            preferredContactMethod === 'email' && SIGNUP_EMAIL_REGEX.test(emailTrim)
              ? emailTrim
              : '',
          ...(digits.length >= 10 ? { phone: digits } : {}),
        };
        const fingerprint = {
          firstName: patchPayload.firstName,
          lastName: patchPayload.lastName,
          email: patchPayload.email || '',
          phone: patchPayload.phone != null ? String(patchPayload.phone) : '',
        };

        try {
          await serviceWizardSearchRequestService.patchRequester(res.id, {
            clientSessionId,
            ...patchPayload,
          });
          writeWizardRequesterCache(res.id, {
            firstName: patchPayload.firstName,
            lastName: patchPayload.lastName,
            email: patchPayload.email || '',
            phoneInput: phone,
            savedPatchFingerprint: fingerprint,
          });
        } catch (patchErr) {
          if (patchErr.status === 403) {
            setPendingResume({ requestId: res.id, clientSessionId });
            setEmailHasAccount(true);
            setSubmitting(false);
            return;
          }
          throw patchErr;
        }
      }

      const sendBody = {
        businessId: resolvedBusinessId,
        preferredContactMethod,
        ...(clientSessionId && !user ? { clientSessionId } : {}),
      };

      try {
        await serviceWizardSearchRequestService.sendToBusiness(res.id, sendBody);
      } catch (sendErr) {
        if (
          sendErr.status === 409 ||
          /already sent/i.test(String(sendErr.message || ''))
        ) {
          setError(
            'You have already sent an inquiry for this service. The business will reach out when they can.',
          );
          setSubmitting(false);
          return;
        }
        throw sendErr;
      }

      reset();
      onSent?.({ requestId: res.id, clientSessionId });
    } catch (err) {
      setError(
        err.message || 'Could not send your inquiry. Please try again.',
      );
      setSubmitting(false);
    }
  };

  const guestBlockedByExistingAccount = !user && emailHasAccount;
  const submitDisabled =
    submitting ||
    guestBlockedByExistingAccount ||
    contactMethodLoading;

  const phoneSelected = preferredContactMethod === 'phone' || preferredContactMethod === 'text';
  const emailField = (
    <ContactField
      key="email-field"
      className="guest-form-full-row"
      label="Email"
      type="email"
      name="email"
      value={email}
      onChange={handleEmailChange}
      error={Boolean(guestFieldErrors.email)}
      helperText={guestFieldErrors.email || undefined}
      required={preferredContactMethod === 'email'}
      fullWidth
      variant="outlined"
      size="small"
      autoComplete="email"
    />
  );
  const phoneField = (
    <ContactField
      key="phone-field"
      className="guest-form-full-row"
      label={`Phone${preferredContactMethod === 'email' ? ' (optional)' : ''}`}
      name="phone"
      value={phone}
      onChange={handlePhoneChange}
      onBlur={() =>
        setGuestFieldErrors((prev) => ({
          ...prev,
          phone:
            preferredContactMethod === 'phone' || preferredContactMethod === 'text'
              ? getSignupPhoneError(phone)
              : optionalPhoneError(phone),
        }))
      }
      error={Boolean(guestFieldErrors.phone)}
      helperText={guestFieldErrors.phone || undefined}
      required={phoneSelected}
      fullWidth
      variant="outlined"
      size="small"
      placeholder="555-123-4567"
      inputMode="tel"
      autoComplete="tel"
    />
  );

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogHeader>
        <HeaderMain>
          <HeaderIconWrap aria-hidden>
            <MailOutlineRoundedIcon sx={{ fontSize: 26 }} />
          </HeaderIconWrap>
          <div>
            <HeaderTitle component="h2">Send inquiry</HeaderTitle>
            <HeaderSubtitle component="p">{serviceTitle}</HeaderSubtitle>
          </div>
        </HeaderMain>
        <DialogCloseButton onClick={handleClose} aria-label="Close" size="small">
          <CloseIcon fontSize="small" />
        </DialogCloseButton>
      </DialogHeader>

      <DialogBody>
        {!user && guestBlockedByExistingAccount && (
          <ContactExistingAccountPanel role="status">
            <ContactExistingAccountIconWrap aria-hidden>
              <LoginRoundedIcon />
            </ContactExistingAccountIconWrap>
            <ContactExistingAccountBody>
              <ContactExistingAccountTitle>Account found</ContactExistingAccountTitle>
              <ContactExistingAccountMessage>
                An account already exists for this email. Sign in to send your inquiry with your
                saved profile—we&apos;ll return you here after you log in.
              </ContactExistingAccountMessage>
              <ContactSignInButton
                variant="contained"
                disableElevation
                onClick={() =>
                  handleGoToSignIn(
                    pendingResume?.requestId,
                    pendingResume?.clientSessionId,
                  )
                }
                disabled={!pendingResume?.requestId}
              >
                Sign in
              </ContactSignInButton>
            </ContactExistingAccountBody>
          </ContactExistingAccountPanel>
        )}

        <UrgencySection>
          <SectionLabel component="span">When do you need help?</SectionLabel>
          <UrgencyOptionsList role="group" aria-label="Urgency">
            {WIZARD_URGENCY_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isSelected = urgency === option.id;
              return (
                <UrgencyOptionButton
                  key={option.id}
                  type="button"
                  $selected={isSelected}
                  $urgent={Boolean(option.urgent)}
                  onClick={() => setUrgency(option.id)}
                  aria-pressed={isSelected}
                  disabled={guestBlockedByExistingAccount}
                >
                  <span className="option-icon">
                    <Icon sx={{ fontSize: 15 }} />
                  </span>
                  <span className="option-label">{option.label}</span>
                </UrgencyOptionButton>
              );
            })}
          </UrgencyOptionsList>
        </UrgencySection>

        <LocationSection>
          <SectionLabel component="span">Where is the work?</SectionLabel>
          <CityStateRow>
            <DetailsField
              label="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Fort Thomas"
              fullWidth
              size="small"
              disabled={guestBlockedByExistingAccount}
              autoComplete="address-level2"
            />
            <DetailsField
              select
              label="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
              fullWidth
              size="small"
              disabled={guestBlockedByExistingAccount}
              autoComplete="address-level1"
            >
              {SERVICE_AREA_STATE_CODES.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </DetailsField>
          </CityStateRow>
          <LocationHint>
            City and state only — no street address required.
          </LocationHint>
        </LocationSection>

        <DetailsSection>
          <SectionLabel component="span">What do you need?</SectionLabel>
          <DetailsField
            multiline
            minRows={4}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Describe your project, timeline, and anything that would help the business respond."
            fullWidth
            size="small"
            disabled={guestBlockedByExistingAccount}
          />
        </DetailsSection>

        <PhotosSection>
          <SectionLabel component="span">Photos (optional)</SectionLabel>
          <PhotoRow>
            {photos.map((photo, index) => (
              <PhotoThumb key={`${photo.name}-${index}`}>
                <img src={photo.preview} alt={`Upload ${index + 1}`} />
                <button type="button" onClick={() => removePhoto(index)} aria-label="Remove photo">
                  ×
                </button>
              </PhotoThumb>
            ))}
            {photos.length < 5 && !guestBlockedByExistingAccount ? (
              <AddPhotoButton type="button" onClick={() => fileInputRef.current?.click()}>
                <AddPhotoAlternateIcon sx={{ fontSize: 20 }} />
                Add
              </AddPhotoButton>
            ) : null}
          </PhotoRow>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            multiple
            hidden
            onChange={handlePhotoUpload}
          />
        </PhotosSection>

        <ContactMethodSection>
          {contactMethodLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
              <NkySpinner size="sm" label="Loading preferred contact method" />
            </Box>
          ) : contactMethodLocked ? (
            <>
              <ContactMethodSavedNote>
                Your preferred contact method is{' '}
                <strong>{preferredContactMethodLabel(preferredContactMethod)}</strong>.
              </ContactMethodSavedNote>
              <ContactMethodSavedNote>
                If you would like to edit this, you can do so on the{' '}
                <Link to="/account" target="_blank" rel="noopener noreferrer">
                  account page
                </Link>
                .
              </ContactMethodSavedNote>
            </>
          ) : (
            <>
              <ContactMethodLabel>How should they contact you?</ContactMethodLabel>
              <ContactMethodOptions role="radiogroup" aria-label="Preferred contact method">
                <ContactMethodButton
                  type="button"
                  role="radio"
                  aria-checked={preferredContactMethod === 'email'}
                  $selected={preferredContactMethod === 'email'}
                  onClick={() => handlePreferredContactMethodChange('email')}
                  disabled={guestBlockedByExistingAccount}
                >
                  <EmailRoundedIcon />
                  Email
                </ContactMethodButton>
                <ContactMethodButton
                  type="button"
                  role="radio"
                  aria-checked={preferredContactMethod === 'phone'}
                  $selected={preferredContactMethod === 'phone'}
                  onClick={() => handlePreferredContactMethodChange('phone')}
                  disabled={guestBlockedByExistingAccount}
                >
                  <LocalPhoneRoundedIcon />
                  Phone
                </ContactMethodButton>
                <ContactMethodButton
                  type="button"
                  role="radio"
                  aria-checked={preferredContactMethod === 'text'}
                  $selected={preferredContactMethod === 'text'}
                  onClick={() => handlePreferredContactMethodChange('text')}
                  disabled={guestBlockedByExistingAccount}
                >
                  <SmsRoundedIcon />
                  Text
                </ContactMethodButton>
              </ContactMethodOptions>
            </>
          )}
        </ContactMethodSection>

        {!user && !guestBlockedByExistingAccount && (
          <GuestFormSection sx={{ mt: 2 }}>
            <SectionLabel component="span" sx={{ mb: 1.5, display: 'block' }}>
              Your contact info
            </SectionLabel>
            <GuestFormGrid>
              <ContactField
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onBlur={(e) => {
                  const normalized = toPersonNameCase(e.target.value);
                  if (normalized !== e.target.value) setFirstName(normalized);
                }}
                required
                fullWidth
                variant="outlined"
                size="small"
              />
              <ContactField
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onBlur={(e) => {
                  const normalized = toPersonNameCase(e.target.value);
                  if (normalized !== e.target.value) setLastName(normalized);
                }}
                required
                fullWidth
                variant="outlined"
                size="small"
              />
              {phoneSelected ? (
                phoneField
              ) : (
                <>
                  {emailField}
                  {phoneField}
                </>
              )}
              <Box className="guest-form-full-row" sx={{ mt: -0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="p"
                  sx={{ m: 0, lineHeight: 1.45 }}
                >
                  {preferredContactMethod === 'email' &&
                    'Email is required since that’s your preferred contact method. Phone is optional.'}
                  {phoneSelected &&
                    'Phone is required since that’s your preferred contact method.'}
                  {!preferredContactMethod &&
                    'Select how you’d like to be contacted above, then fill in that field below.'}
                </Typography>
              </Box>
            </GuestFormGrid>
          </GuestFormSection>
        )}

        <ContactDisclaimerText>
          By submitting this form, your contact information will be shared with{' '}
          {provider?.BusinessName || 'the business'} to fulfill your service request. See our{' '}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Link>
          .
        </ContactDisclaimerText>

        {error ? (
          <FormErrorAlert severity="error" variant="filled">
            {error}
          </FormErrorAlert>
        ) : null}
      </DialogBody>

      <DialogFooter>
        <SecondaryButton variant="outlined" onClick={handleClose} disabled={submitting}>
          Cancel
        </SecondaryButton>
        <PrimaryButton variant="contained" disableElevation onClick={handleSubmit} disabled={submitDisabled}>
          {submitting ? (
            <NkySpinner size="sm" onDark />
          ) : (
            <>
              <SendIcon sx={{ fontSize: 18 }} /> Send inquiry
            </>
          )}
        </PrimaryButton>
      </DialogFooter>
    </StyledDialog>
  );
}
